/**
 * Image Generation API Client
 * 
 * Provides functions to generate images using the direct API endpoint
 * that bypasses ADK function calling to avoid token limit issues.
 */

import config from './config';
import { getBeHeaders } from './utils/apiHeaders';
import { getAuthorizedFetch } from './utils/fetchRegistry';

/**
 * Generate images using the direct API endpoint.
 * 
 * @param {string} prompt - Text description of the image to generate
 * @param {string} aspectRatio - Aspect ratio (1:1, 16:9, 9:16, 4:3, 3:4)
 * @param {number} numberOfImages - Number of images to generate (1-4)
 * @returns {Promise<Object>} Response with generated images or error
 */
export async function generateImage(prompt, aspectRatio = '1:1', numberOfImages = 1) {
    const baseUrl = config.agentUrl || 'http://localhost:8000';
    
    try {
        const response = await getAuthorizedFetch()(`${baseUrl}/api/generate-image`, {
            method: 'POST',
            headers: getBeHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                prompt,
                aspect_ratio: aspectRatio,
                number_of_images: numberOfImages,
            }),
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            return {
                success: false,
                message: errorData.detail || 'Failed to generate image',
            };
        }
        
        const data = await response.json();
        
        return {
            success: true,
            data: data,
        };
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Network error while generating image',
        };
    }
}

/**
 * Extract image generation request from user message.
 * Detects if the user is asking to generate an image.
 * 
 * @param {string} message - User's message
 * @returns {Object|null} Image generation params or null if not an image request
 */
export function detectImageGenerationRequest(message) {
    const lowerMessage = message.toLowerCase();
    
    // Check for image generation keywords
    const imageKeywords = [
        'generate image',
        'generate an image',
        'create image',
        'create an image',
        'make image',
        'make an image',
        'draw image',
        'draw an image',
        'generate picture',
        'create picture',
    ];
    
    const isImageRequest = imageKeywords.some(keyword => lowerMessage.includes(keyword));
    
    if (!isImageRequest) {
        return null;
    }
    
    // Extract prompt (text after the command)
    let prompt = message;
    for (const keyword of imageKeywords) {
        const regex = new RegExp(keyword + '\\s*(of|:)?\\s*', 'i');
        prompt = prompt.replace(regex, '').trim();
    }
    
    // Extract aspect ratio if mentioned
    let aspectRatio = '1:1';
    const ratioPatterns = [
        /\b(16:9|16x9|landscape|wide)\b/i,
        /\b(9:16|9x16|portrait|tall)\b/i,
        /\b(4:3|4x3)\b/i,
        /\b(3:4|3x4)\b/i,
        /\b(1:1|1x1|square)\b/i,
    ];
    
    const ratioMap = {
        '16:9': '16:9', '16x9': '16:9', 'landscape': '16:9', 'wide': '16:9',
        '9:16': '9:16', '9x16': '9:16', 'portrait': '9:16', 'tall': '9:16',
        '4:3': '4:3', '4x3': '4:3',
        '3:4': '3:4', '3x4': '3:4',
        '1:1': '1:1', '1x1': '1:1', 'square': '1:1',
    };
    
    for (const pattern of ratioPatterns) {
        const match = message.match(pattern);
        if (match) {
            aspectRatio = ratioMap[match[1].toLowerCase()] || '1:1';
            // Remove aspect ratio from prompt
            prompt = prompt.replace(pattern, '').trim();
            break;
        }
    }
    
    // Extract number of images if mentioned
    let numberOfImages = 1;
    const numberPattern = /\b(\d+)\s+(images?|variations?|versions?)\b/i;
    const numberMatch = message.match(numberPattern);
    if (numberMatch) {
        const num = parseInt(numberMatch[1], 10);
        if (num >= 1 && num <= 4) {
            numberOfImages = num;
            // Remove number specification from prompt
            prompt = prompt.replace(numberPattern, '').trim();
        }
    }
    
    // Clean up prompt
    prompt = prompt.replace(/\s+/g, ' ').trim();
    
    if (!prompt) {
        return null; // No actual prompt provided
    }
    
    return {
        prompt,
        aspectRatio,
        numberOfImages,
    };
}
